import React, { useState, useEffect, useCallback } from 'react';
import { Sparkles, Upload, AlertTriangle, Video, Eye } from 'lucide-react';
import apiClient from '../services/api';

const AICameraAnalyzer = () => {
  const [file, setFile] = useState(null);
  const [prompt, setPrompt] = useState('Detecte personnes, vehicules, incendies, intrusions et comportements suspects. Reponse concise en francais avec un niveau de risque (faible/moyen/eleve).');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);
  const [cameras, setCameras] = useState([]);
  const [monitorResults, setMonitorResults] = useState({});
  const [snapshotFailures, setSnapshotFailures] = useState({});

  const loadCameras = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/cameras/');
      setCameras(Array.isArray(data) ? data : (data.cameras || []));
    } catch { /* ignore */ }
  }, []);

  const loadMonitor = useCallback(async () => {
    try {
      const { data } = await apiClient.get('/ai-camera/monitor/results');
      setMonitorResults(data.results || {});
      setSnapshotFailures(data.snapshot_failures || {});
    } catch { /* ignore */ }
  }, []);

  useEffect(() => {
    loadCameras();
    loadMonitor();
    const t = setInterval(loadMonitor, 8000);
    return () => clearInterval(t);
  }, [loadCameras, loadMonitor]);

  const toggleIA = async (camId, enabled) => {
    try {
      await apiClient.post(`/ai-camera/cameras/${camId}/toggle-ia`, { ia_enabled: enabled });
      loadCameras();
    } catch {
      alert("Impossible d'activer l'IA sur cette camera");
    }
  };

  const onFile = (e) => {
    const f = e.target.files?.[0];
    setFile(f || null);
    if (f) {
      const reader = new FileReader();
      reader.onload = () => setPreview(reader.result);
      reader.readAsDataURL(f);
    }
  };

  const analyze = async () => {
    if (!file) return;
    setLoading(true); setResult(null);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('prompt', prompt);
      const { data } = await apiClient.post('/ai-camera/analyze', fd, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      setResult(data);
    } catch (e) {
      setResult({ ok: false, error: e?.response?.data?.detail || 'Erreur analyse' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white rounded-xl border border-slate-200 p-5 shadow-sm" data-testid="ai-camera-widget">
      <div className="flex items-center gap-2 mb-4">
        <Sparkles className="w-5 h-5 text-cyan-600" />
        <h3 className="text-sm font-semibold text-slate-800">IA Camera - NVIDIA VLM</h3>
        <span className="text-[10px] px-2 py-0.5 rounded-full bg-cyan-50 text-cyan-700">meta/llama-3.2-11b-vision</span>
      </div>

      <label className="block">
        <span className="text-xs text-slate-600">Capture ou snapshot camera (JPEG/PNG, max 5MB)</span>
        <div className="mt-1 flex items-center gap-2">
          <input type="file" accept="image/*" onChange={onFile} className="text-xs" data-testid="ai-cam-file" />
        </div>
      </label>

      <label className="block mt-3">
        <span className="text-xs text-slate-600">Prompt (question a l'IA)</span>
        <textarea
          value={prompt}
          onChange={(e) => setPrompt(e.target.value)}
          rows={2}
          className="mt-1 w-full text-xs border border-slate-200 rounded-lg p-2"
          data-testid="ai-cam-prompt"
        />
      </label>

      <button
        onClick={analyze}
        disabled={!file || loading}
        className="mt-3 px-4 py-2 rounded-lg bg-cyan-600 text-white text-sm hover:bg-cyan-700 disabled:opacity-50 inline-flex items-center gap-2"
        data-testid="ai-cam-analyze"
      >
        <Upload className="w-4 h-4" />
        {loading ? 'Analyse...' : 'Analyser'}
      </button>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        {preview && (
          <div>
            <p className="text-xs text-slate-500 mb-1">Preview</p>
            <img src={preview} alt="preview" className="w-full max-h-64 object-contain rounded-lg border border-slate-200" />
          </div>
        )}
        {result && (
          <div>
            <p className="text-xs text-slate-500 mb-1">Resultat</p>
            {result.ok ? (
              <div className="p-3 bg-emerald-50 border border-emerald-100 rounded-lg text-sm text-slate-800" data-testid="ai-cam-result">
                {result.description}
                <p className="text-[10px] text-slate-400 mt-2">Modele: {result.model}</p>
              </div>
            ) : (
              <div className="p-3 bg-red-50 border border-red-100 rounded-lg text-sm text-red-700 flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {result.error || result.reason}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Section monitoring continu par camera */}
      <div className="mt-6 pt-4 border-t border-slate-100">
        <div className="flex items-center gap-2 mb-2">
          <Video className="w-4 h-4 text-slate-600" />
          <h4 className="text-xs font-semibold text-slate-800 uppercase tracking-wide">Monitoring continu (5s)</h4>
        </div>
        {cameras.length === 0 ? (
          <p className="text-xs text-slate-500">Aucune camera enregistree.</p>
        ) : (
          <div className="space-y-2">
            {cameras.slice(0, 8).map((c) => {
              const r = monitorResults[c.id];
              const risk = r?.risk;
              const failures = snapshotFailures[c.id] || 0;
              const offline = failures >= 3;
              const badge = risk === 'eleve' ? 'bg-red-100 text-red-700'
                : risk === 'moyen' ? 'bg-amber-100 text-amber-700'
                : risk === 'faible' ? 'bg-emerald-100 text-emerald-700'
                : 'bg-slate-100 text-slate-500';
              return (
                <div key={c.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-50" data-testid={`vlm-cam-${c.id}`}>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      checked={!!c.ia_enabled}
                      onChange={(e) => toggleIA(c.id, e.target.checked)}
                      className="w-4 h-4 accent-cyan-600"
                      data-testid={`vlm-toggle-${c.id}`}
                    />
                    <span className="text-sm text-slate-700">{c.name || c.nom || c.id.slice(0,8)}</span>
                  </label>
                  {offline && (
                    <span className="text-[10px] px-2 py-0.5 rounded-full bg-orange-100 text-orange-700 font-semibold" title={`${failures} snapshots consecutifs echoues`} data-testid={`cam-offline-${c.id}`}>
                      OFFLINE ({failures})
                    </span>
                  )}
                  {c.ia_enabled && !offline && (
                    <span className={`text-[10px] px-2 py-0.5 rounded-full ${badge}`}>
                      {risk || 'en attente'}
                    </span>
                  )}
                  {r?.description && (
                    <span className="text-xs text-slate-500 truncate flex-1" title={r.description}>
                      <Eye className="w-3 h-3 inline mr-1" />
                      {String(r.description).slice(0, 120)}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default AICameraAnalyzer;
